import authApiService from "./userApiService";

// Helper function to handle errors
const handleError = (error) => {
  console.error('Tournament Leaderboard API Error:', error);
  return {
    success: false,
    error: error.response?.data?.message || error.response?.data?.error || error.message || 'An unexpected error occurred',
    statusCode: error.response?.status,
    details: error.response?.data?.details || null
  };
};

// Helper function to handle successful responses
const handleSuccess = (response, message = null) => {
  // Handle the specific response format from our API
  const apiData = response.data?.body || response.data;
  return {
    success: true,
    data: apiData?.data || apiData,
    message: message || apiData?.message || 'Operation completed successfully',
    statusCode: response.status
  };
};

const tournamentLeaderboardService = {
  // ============================================================================
  // FILE UPLOAD OPERATIONS
  // ============================================================================

  // Upload Excel file for tournament leaderboard processing
  async uploadExcelFile(file) {
    try {
      const formData = new FormData();
      formData.append('excelFile', file);

      const response = await authApiService.post("/tournament-leaderboard/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return handleSuccess(response, 'Excel file uploaded and processed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Upload multiple Excel files for multi-flight tournaments
  async uploadMultipleExcelFiles(files) {
    try {
      const formData = new FormData();
      
      // Add all files to FormData
      files.forEach((file, index) => {
        formData.append('excelFiles', file);
      });

      const response = await authApiService.post("/tournament-leaderboard/upload-multiple", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return handleSuccess(response, 'Multiple Excel files uploaded and processed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Process tournament files from a folder
  async processTournamentFolder(folderPath) {
    try {
      const response = await authApiService.post("/tournament-leaderboard/process-folder", {
        folderPath: folderPath
      });
      
      return handleSuccess(response, 'Tournament folder processed successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Generate final combined leaderboard
  async generateFinalLeaderboard() {
    try {
      console.log('Generating final leaderboard...');
      const response = await authApiService.get("/tournament-leaderboard/final-leaderboard", {
        responseType: 'blob'
      });
      
      console.log('Final leaderboard response:', response);
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty response from server');
      }
      
      // Create blob URL for download
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `final-combined-leaderboard-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Final combined leaderboard generated and downloaded successfully'
      };
    } catch (error) {
      console.error('Final leaderboard generation error:', error);
      return handleError(error);
    }
  },

  // Delete specific tournament
  async deleteTournament(tournamentName) {
    try {
      const encodedTournamentName = encodeURIComponent(tournamentName);
      const response = await authApiService.delete(`/tournament-leaderboard/tournament/${encodedTournamentName}`);
      return handleSuccess(response, 'Tournament deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete file by eventKey or filename
  async deleteFile(eventKey, filename) {
    try {
      const queryParams = new URLSearchParams();
      if (eventKey) queryParams.append('eventKey', eventKey);
      if (filename) queryParams.append('filename', filename);
      
      const response = await authApiService.delete(`/tournament-leaderboard/file?${queryParams.toString()}`);
      return handleSuccess(response, 'File deleted successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // LEADERBOARD DATA OPERATIONS
  // ============================================================================

  // Get tournament leaderboard with filtering and pagination
  async getTournamentLeaderboard(params = {}) {
    try {
      // If tournamentDate is provided, use cumulative endpoint for date-based filtering
      if (params.tournamentDate) {
        return await this.getCumulativeLeaderboardByDate(params.tournamentDate, params);
      }

      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filter parameters
      if (params.tier) queryParams.append('tier', params.tier);
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);
      
      // Add sorting parameters
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await authApiService.get(`/tournament-leaderboard/public/leaderboard?${queryParams.toString()}`);
      return handleSuccess(response, 'Tournament leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get cumulative leaderboard by date
  async getCumulativeLeaderboardByDate(endDate, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('endDate', endDate);
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      // Add additional filters
      if (params.tier) queryParams.append('tier', params.tier);
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);

      const response = await authApiService.get(`/tournament-leaderboard/cumulative?${queryParams.toString()}`);
      return handleSuccess(response, 'Cumulative leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get leaderboard by date range (start to end)
  async getLeaderboardByDateRange(startDate, endDate, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('startDate', startDate);
      queryParams.append('endDate', endDate);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);

      const response = await authApiService.get(`/tournament-leaderboard/public/leaderboard/range?${queryParams.toString()}`);
      return handleSuccess(response, 'Date-range leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get monthly leaderboard (v2 from per-tournament entries)
  async getMonthlyLeaderboardV2(year, month, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);
      const query = queryParams.toString();
      const response = await authApiService.get(`/tournament-leaderboard/public/leaderboard/monthly2/${year}/${month}${query ? `?${query}` : ''}`);
      return handleSuccess(response, 'Monthly leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get yearly leaderboard (from per-tournament entries)
  async getYearlyLeaderboard(year, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);
      const query = queryParams.toString();
      const response = await authApiService.get(`/tournament-leaderboard/public/leaderboard/yearly/${year}${query ? `?${query}` : ''}`);
      return handleSuccess(response, 'Yearly leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get public tournament leaderboard (no authentication required)
  async getPublicTournamentLeaderboard(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.tier) queryParams.append('tier', params.tier);
      if (params.playerName) queryParams.append('playerName', params.playerName);
      if (params.tournamentName) queryParams.append('tournamentName', params.tournamentName);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await authApiService.get(`/tournament-leaderboard/public/leaderboard?${queryParams.toString()}`);
      return handleSuccess(response, 'Public tournament leaderboard retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // STATISTICS OPERATIONS
  // ============================================================================

  // Get tournament statistics
  async getTournamentStats() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/public/stats");
      return handleSuccess(response, 'Tournament statistics retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get uploaded tournaments list
  async getUploadedTournaments() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/public/tournaments");
      return handleSuccess(response, 'Uploaded tournaments retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get tournament calendar
  async getTournamentCalendar() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/public/calendar");
      return handleSuccess(response, 'Tournament calendar retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  async getUploadedFileHistory() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/files");
      return handleSuccess(response, "Uploaded file history retrieved successfully");
    } catch (error) {
      return handleError(error);
    }
  },

  async downloadUploadedFile(id, filename) {
    try {
      const response = await authApiService.get(`/tournament-leaderboard/files/${id}/download`, {
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.message || parsed.error || "Failed to download file");
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "leaderboard.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: "File downloaded successfully" };
    } catch (error) {
      return handleError(error);
    }
  },

  async downloadAllUploadedFiles() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/files/download-all", {
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.message || parsed.error || "Failed to download files");
      }

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tournament-excel-files.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: "Files downloaded successfully" };
    } catch (error) {
      return handleError(error);
    }
  },

  // Get public tournament statistics
  async getPublicTournamentStats() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/public/stats");
      return handleSuccess(response, 'Public tournament statistics retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // PLAYER DETAILS OPERATIONS
  // ============================================================================

  // Get detailed player information
  async getPlayerDetails(playerName, playerId) {
    try {
      const response = await authApiService.get(`/tournament-leaderboard/public/player/${encodeURIComponent(playerName)}/${playerId}`);
      return handleSuccess(response, 'Player details retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Get player tournament details
  async getPlayerTournamentDetails(playerName, playerId, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      const query = queryParams.toString();
      const response = await authApiService.get(`/tournament-leaderboard/public/player/${encodeURIComponent(playerName)}/${playerId}/tournaments${query ? `?${query}` : ''}`);
      return handleSuccess(response, 'Player tournament details retrieved successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // ============================================================================
  // REPORT GENERATION OPERATIONS
  // ============================================================================

  // Generate HTML report
  async generateHTMLReport() {
    try {
      console.log('Generating HTML report...');
      const response = await authApiService.get("/tournament-leaderboard/report/html", {
        responseType: 'blob'
      });
      
      console.log('HTML report response:', response);
      
      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty response from server');
      }
      
      // Create blob URL for download
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `tournament-leaderboard-report-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'HTML report generated and downloaded successfully'
      };
    } catch (error) {
      console.error('HTML report generation error:', error);
      return handleError(error);
    }
  },

  // ============================================================================
  // VALIDATION AND UTILITY METHODS
  // ============================================================================

  // Validate file upload
  validateFileUpload(file) {
    const errors = [];
    
    if (!file) {
      errors.push('No file selected');
      return { isValid: false, errors };
    }

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('Only Excel files (.xlsx, .xls) are allowed');
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      errors.push('File size must be less than 10MB');
    }

    // Only basic checks (type and size); no filename format enforcement

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Validate multiple file uploads
  validateMultipleFileUpload(files) {
    const errors = [];
    
    if (!files || files.length === 0) {
      errors.push('No files selected');
      return { isValid: false, errors };
    }

    if (files.length > 20) {
      errors.push('Maximum 20 files allowed per upload');
    }

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    const maxSize = 10 * 1024 * 1024; // 10MB per file

    Array.from(files).forEach((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`File ${index + 1} (${file.name}): Only Excel files (.xlsx, .xls) are allowed`);
      }

      if (file.size > maxSize) {
        errors.push(`File ${index + 1} (${file.name}): File size must be less than 10MB`);
      }

      // No filename format enforcement for multi upload either
    });

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  // Format leaderboard data for display
  formatLeaderboardData(data) {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((player, index) => ({
      rank: index + 1,
      playerName: player.playerName || '',
      playerId: player.playerId || '',
      totalPoints: player.totalPoints || 0,
      registrationPoints: player.registrationPoints || 0,
      tournamentPoints: player.tournamentPoints || 0,
      tournamentsPlayed: player.tournamentsPlayed || 0,
      tier: player.tier || 'under10k',
      place: player.place || null,
      tournamentName: player.tournamentName || '',
      buyIn: player.buyIn || '',
      prizePool: player.prizePool || '',
      guarantee: player.guarantee || '',
      createdAt: player.createdAt || null,
      points: player.points || 0
    }));
  },

  // Calculate leaderboard statistics
  calculateLeaderboardStats(leaderboard) {
    if (!leaderboard || leaderboard.length === 0) {
      return {
        totalPlayers: 0,
        totalTournaments: 0,
        totalPoints: 0,
        averagePoints: 0,
        highestScore: 0,
        lowestScore: 0,
        tierDistribution: {},
        topPlayers: []
      };
    }

    const totalPoints = leaderboard.reduce((sum, player) => sum + (player.totalPoints || 0), 0);
    const points = leaderboard.map(player => player.totalPoints || 0);
    
    const tierDistribution = leaderboard.reduce((dist, player) => {
      const tier = player.tier || 'under10k';
      dist[tier] = (dist[tier] || 0) + 1;
      return dist;
    }, {});

    const topPlayers = leaderboard.slice(0, 10).map((player, index) => ({
      rank: index + 1,
      name: player.playerName,
      points: player.totalPoints,
      tournaments: player.tournamentsPlayed
    }));

    return {
      totalPlayers: leaderboard.length,
      totalTournaments: [...new Set(leaderboard.map(p => p.tournamentName))].length,
      totalPoints: totalPoints,
      averagePoints: Math.round(totalPoints / leaderboard.length),
      highestScore: Math.max(...points),
      lowestScore: Math.min(...points),
      tierDistribution: tierDistribution,
      topPlayers: topPlayers
    };
  },

  // Filter options for leaderboard
  getFilterOptions() {
    return {
      tiers: [
        { value: 'freeroll', label: 'Freeroll' },
        { value: 'under10k', label: 'Under $10K' },
        { value: '10k-25k', label: '$10K - $25K' },
        { value: '25k+', label: '$25K+' }
      ],
      sortOptions: [
        { value: 'totalPoints', label: 'Total Points' },
        { value: 'registrationPoints', label: 'Registration Points' },
        { value: 'tournamentPoints', label: 'Tournament Points' },
        { value: 'tournamentsPlayed', label: 'Tournaments Played' },
        { value: 'playerName', label: 'Player Name' },
        { value: 'createdAt', label: 'Date Created' }
      ],
      sortOrders: [
        { value: 'DESC', label: 'Descending' },
        { value: 'ASC', label: 'Ascending' }
      ]
    };
  },

  // Export leaderboard data as CSV
  exportToCSV(leaderboard, filename = 'tournament-leaderboard') {
    if (!leaderboard || leaderboard.length === 0) {
      return;
    }

    const headers = [
      'Rank',
      'Player Name',
      'Player ID',
      'Total Points',
      'Registration Points',
      'Tournament Points',
      'Tournaments Played',
      'Tier',
      'Best Place',
      'Tournament Name',
      'Buy-In',
      'Prize Pool',
      'Guarantee',
      'Created At'
    ];

    const csvContent = [
      headers.join(','),
      ...leaderboard.map((player, index) => [
        index + 1,
        `"${player.playerName || ''}"`,
        `"${player.playerId || ''}"`,
        player.totalPoints || 0,
        player.registrationPoints || 0,
        player.tournamentPoints || 0,
        player.tournamentsPlayed || 0,
        `"${player.tier || 'under10k'}"`,
        player.place || '',
        `"${player.tournamentName || ''}"`,
        `"${player.buyIn || ''}"`,
        `"${player.prizePool || ''}"`,
        `"${player.guarantee || ''}"`,
        `"${player.createdAt || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Clear all tournament data
  async clearTournamentData() {
    try {
      const response = await authApiService.delete("/tournament-leaderboard/clear");
      return handleSuccess(response, 'Tournament data cleared successfully');
    } catch (error) {
      return handleError(error);
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await authApiService.get("/tournament-leaderboard/public/stats");
      return handleSuccess(response, 'Tournament leaderboard service is healthy');
    } catch (error) {
      return handleError(error);
    }
  }
};

export default tournamentLeaderboardService;
